/**
 * 圖表配色（dataviz reference palette，light／dark 各自驗證過的 step）。
 * 月別狀態圖改用語意色（getStatusColor）；CATEGORICAL 只剩圓餅基色使用。
 * 文字、格線、圓餅間隔改由 CSS token 覆寫（見 DASHBOARD_CHART_CLASS）。
 */
const CATEGORICAL = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};

/**
 * 狀態語意色（對應 DASHBOARD_TONES）：第一階＝狀態卡圖例圓點色（gray-400／amber-500／sky-500／emerald-500／red-500），
 * 同一 module 有多個狀態落在同一色系時，依 statusOrder 往後取較深（light）或較淺（dark）的一階以便區分。
 */
const TONE_COLORS = {
  light: {
    pending: ['#9ca3af', '#4b5563'],
    warn: ['#f59e0b', '#b45309'],
    active: ['#0ea5e9', '#0369a1'],
    done: ['#10b981', '#047857'],
    alert: ['#ef4444', '#b91c1c'],
  },
  dark: {
    pending: ['#9ca3af', '#d1d5db'],
    warn: ['#f59e0b', '#fcd34d'],
    active: ['#0ea5e9', '#7dd3fc'],
    done: ['#10b981', '#6ee7b7'],
    alert: ['#ef4444', '#fca5a5'],
  },
};

/**
 * 狀態 → 語意色。`statusTones` 沒定義的狀態退回 pending 灰。
 * @param {string} status
 * @param {{ statusOrder: string[], statusTones?: Record<string, string> }} config
 */
export function getStatusColor(resolvedTheme, status, { statusOrder, statusTones = {} }) {
  const tone = statusTones[status] ?? 'pending';
  const shades = TONE_COLORS[resolvedTheme === 'dark' ? 'dark' : 'light'][tone] ?? TONE_COLORS.light.pending;
  const shadeIndex = statusOrder
    .filter((item) => (statusTones[item] ?? 'pending') === tone)
    .indexOf(status);
  return shades[Math.max(shadeIndex, 0) % shades.length];
}

export function getSeriesColor(resolvedTheme, index) {
  const palette = CATEGORICAL[resolvedTheme === 'dark' ? 'dark' : 'light'];
  return palette[index % palette.length];
}

/** Monochrome pie 基色（slot 1 blue）；light 往深色、dark 往淺色漸層，避免貼近背景 */
export function getMonochromeOptions(resolvedTheme) {
  const isDark = resolvedTheme === 'dark';
  return {
    enabled: true,
    color: getSeriesColor(resolvedTheme, 0),
    shadeTo: isDark ? 'light' : 'dark',
    shadeIntensity: 0.6,
  };
}

/**
 * ApexCharts 將 foreColor／legend 色寫在 inline attribute；以 token 覆寫，深淺主題皆可讀。
 */
export const DASHBOARD_CHART_CLASS = [
  '[&_.apexcharts-text]:fill-muted-foreground',
  '[&_.apexcharts-legend-text]:text-foreground!',
  '[&_.apexcharts-gridline]:stroke-border',
  '[&_.apexcharts-xaxis-tick]:stroke-border',
  '[&_.apexcharts-pie-area]:stroke-background',
].join(' ');

export function formatCount(value) {
  return new Intl.NumberFormat('zh-TW').format(value ?? 0);
}

export function formatAmount(value) {
  return new Intl.NumberFormat('zh-TW', { maximumFractionDigits: 2 }).format(value ?? 0);
}
